/* ------------------------------------------------------------------
   Pure Focus PDP — purchase-zone controller.

   The pricing engine is fully config-driven: every total, per-can rate,
   SAVE badge, bridge figure and nudge string is derived from the config
   object emitted by the pf-build-pack block (product metafield
   pure_focus.buybox, with Liquid fallbacks). Nothing is hardcoded per
   quantity, and there is no maximum quantity.

   IMPORTANT — charged total: this controller renders the *displayed* price
   and builds the cart line items on the selected plan. Making the *charged*
   cart/checkout total match the ladder to the cent (and persist on ReCharge
   recurring orders) is the developer-owned pricing mechanism — see
   `buildCartItems()` and the integration note in `addToCart()`.
------------------------------------------------------------------ */
(function () {
  'use strict';

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const DEFAULT_CONFIG = {
    minCans: 3,
    pouchesPerCan: 20,
    compareAtPerCan: 16.99,
    subMultiplier: 0.85,
    tiers: [
      { min: 3, anchor: 29.99, rate: 10.0 },
      { min: 6, anchor: 53.99, rate: 9.0 },
      { min: 9, anchor: 73.99, rate: 8.22 }
    ],
    copy: {
      belowMinZero: 'Mix any **{min} flavors** to build your pack',
      belowMin: '**{n} more {unit}** and your pack is ready',
      standard: 'Add **{n} more cans** → ${rate} per can',
      bridge: 'Add **1 more can (${amount})** → ${rate} per can on all {nextCans}',
      max: "You're on the lowest per-can price we offer",
      priceLine1: '{cans} cans · {pouches} pouches',
      priceLine2: '${rate} per can',
      atcEnabled: 'Add To Cart — {qty} Cans →',
      atcDisabled: 'Add {n} More {unitCap} To Continue'
    }
  };

  class PureFocusPdp {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.tiers = this.config.tiers.slice().sort((a, b) => a.min - b.min);
      this.minCans = this.config.minCans;
      this.plan = root.dataset.defaultPlan === 'onetime' ? 'onetime' : 'subscribe';

      this.variants = this.readJson('[data-pf-variants]') || [];
      this.overridePlan = root.dataset.sellingPlan ? parseInt(root.dataset.sellingPlan, 10) : null;

      this.rows = Array.from(root.querySelectorAll('[data-flavor-row]'));
      this.toggleBtns = Array.from(root.querySelectorAll('[data-pf-plan]'));
      this.tierCards = Array.from(root.querySelectorAll('[data-pf-tier]'));
      this.nudge = root.querySelector('[data-pf-nudge]');
      this.nudgeText = root.querySelector('[data-pf-nudge-text]');
      this.atc = root.querySelector('[data-pf-atc]');
      this.atcLabel = root.querySelector('[data-atc-label]');

      this.priceLine1 = root.querySelector('[data-pf-price-line1]');
      this.priceLine2 = root.querySelector('[data-pf-price-line2]');
      this.priceCompare = root.querySelector('[data-pf-compare]');
      this.priceTotal = root.querySelector('[data-pf-total]');
      this.priceBlock = root.querySelector('[data-pf-price-block]');

      this.bindEvents();
      this.render();
    }

    /* ---------- config / data ---------- */
    readJson(selector) {
      const el = this.root.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    readConfig() {
      const parsed = this.readJson('[data-pf-config]') || {};
      const cfg = Object.assign({}, DEFAULT_CONFIG, parsed);
      cfg.copy = Object.assign({}, DEFAULT_CONFIG.copy, parsed.copy || {});
      if (!Array.isArray(cfg.tiers) || !cfg.tiers.length) cfg.tiers = DEFAULT_CONFIG.tiers;
      return cfg;
    }

    money(n) {
      return '$' + Number(n).toFixed(2);
    }

    /* ---------- pricing engine ---------- */
    tierForQty(qty) {
      let match = null;
      for (const tier of this.tiers) {
        if (qty >= tier.min) match = tier;
      }
      return match;
    }

    tierIndexForQty(qty) {
      let idx = -1;
      this.tiers.forEach((tier, i) => {
        if (qty >= tier.min) idx = i;
      });
      return idx;
    }

    oneTimeTotal(qty) {
      const tier = this.tierForQty(qty) || this.tiers[0];
      const eff = Math.max(qty, tier.min);
      return round2(tier.anchor + (eff - tier.min) * tier.rate);
    }

    planTotal(qty, plan) {
      /* Per-can pricing: the total is the per-can rate × qty for both plans, so
         the displayed total matches a per-can discount charged in the cart to
         the cent (subscribe rounds per unit, mirroring ReCharge's per-line %). */
      if (plan === 'subscribe') return round2(this.perCanRate(qty, 'subscribe') * qty);
      return this.oneTimeTotal(qty);
    }

    compareAt(qty) {
      return round2(qty * this.config.compareAtPerCan);
    }

    perCanRate(qty, plan) {
      const tier = this.tierForQty(qty) || this.tiers[0];
      return plan === 'subscribe' ? round2(tier.rate * this.config.subMultiplier) : tier.rate;
    }

    tierRate(index, plan) {
      const tier = this.tiers[index];
      return plan === 'subscribe' ? round2(tier.rate * this.config.subMultiplier) : tier.rate;
    }

    tierSave(index, plan) {
      const tier = this.tiers[index];
      return Math.round(this.compareAt(tier.min) - this.planTotal(tier.min, plan));
    }

    /* ---------- quantities ---------- */
    qtyOf(row) {
      return parseInt(row.dataset.qty, 10) || 0;
    }

    prioOf(row) {
      return parseInt(row.dataset.flavorPriority, 10) || 0;
    }

    totalQty() {
      return this.rows.reduce((sum, row) => sum + this.qtyOf(row), 0);
    }

    setQty(row, qty) {
      row.dataset.qty = Math.max(0, qty);
    }

    addToLowest() {
      let best = null;
      this.rows.forEach((row) => {
        if (!best) {
          best = row;
          return;
        }
        const q = this.qtyOf(row);
        const bq = this.qtyOf(best);
        if (q < bq || (q === bq && this.prioOf(row) < this.prioOf(best))) best = row;
      });
      if (best) this.setQty(best, this.qtyOf(best) + 1);
    }

    removeFromHighest() {
      let best = null;
      this.rows.forEach((row) => {
        if (this.qtyOf(row) <= 0) return;
        if (!best) {
          best = row;
          return;
        }
        const q = this.qtyOf(row);
        const bq = this.qtyOf(best);
        if (q > bq || (q === bq && this.prioOf(row) < this.prioOf(best))) best = row;
      });
      if (best) this.setQty(best, this.qtyOf(best) - 1);
    }

    setPackTo(target) {
      const current = this.totalQty();
      if (target === current) return; /* tapping the active card at its qty does nothing */
      if (target > current) {
        let guard = 0;
        while (this.totalQty() < target && guard < 999) {
          this.addToLowest();
          guard += 1;
        }
      } else {
        let guard = 0;
        while (this.totalQty() > target && guard < 999) {
          this.removeFromHighest();
          guard += 1;
        }
      }
      this.render();
    }

    /* ---------- nudge ---------- */
    formatCopy(template, tokens) {
      let out = escapeHtml(template);
      Object.keys(tokens).forEach((key) => {
        out = out.split('{' + key + '}').join(escapeHtml(String(tokens[key])));
      });
      return out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    }

    nudgeFor(qty, plan) {
      const copy = this.config.copy;
      const maxTier = this.tiers[this.tiers.length - 1];

      if (qty < this.minCans) {
        if (qty === 0) {
          return { state: 'below-min', html: this.formatCopy(copy.belowMinZero, { min: this.minCans }) };
        }
        const remaining = this.minCans - qty;
        return {
          state: 'below-min',
          html: this.formatCopy(copy.belowMin, { n: remaining, unit: remaining === 1 ? 'can' : 'cans' })
        };
      }

      if (qty >= maxTier.min) {
        return { state: 'max', html: this.formatCopy(copy.max, {}) };
      }

      const next = this.tiers.find((tier) => tier.min > qty);
      const nextIndex = this.tiers.indexOf(next);
      const remaining = next.min - qty;
      const rate = this.tierRate(nextIndex, plan).toFixed(2);

      if (remaining === 1) {
        const amount = round2(this.planTotal(next.min, plan) - this.planTotal(qty, plan)).toFixed(2);
        return {
          state: 'bridge',
          html: this.formatCopy(copy.bridge, { amount: amount, rate: rate, nextCans: next.min })
        };
      }
      return { state: 'standard', html: this.formatCopy(copy.standard, { n: remaining, rate: rate }) };
    }

    /* ---------- render ---------- */
    render() {
      const total = this.totalQty();
      const effQty = Math.max(total, this.minCans);
      const frozen = total < this.minCans;
      const plan = this.plan;

      /* Steppers */
      this.rows.forEach((row) => {
        const qty = this.qtyOf(row);
        const display = row.querySelector('[data-qty-display]');
        const minus = row.querySelector('.pf-pdp__step--minus');
        const plus = row.querySelector('.pf-pdp__step--plus');
        if (display) display.textContent = qty;
        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = false;
      });

      /* Tier cards — static per plan, active = last tier reached */
      const activeIndex = this.tierIndexForQty(total);
      this.tierCards.forEach((card) => {
        const index = parseInt(card.dataset.pfTier, 10);
        const save = card.querySelector('[data-tier-save]');
        const rate = card.querySelector('[data-tier-rate]');
        if (save) save.textContent = 'SAVE $' + this.tierSave(index, plan);
        if (rate) rate.textContent = this.money(this.tierRate(index, plan)) + '/can';
        const isActive = index === activeIndex;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      /* Nudge strip */
      const nudge = this.nudgeFor(total, plan);
      if (this.nudge) this.nudge.setAttribute('data-state', nudge.state);
      if (this.nudgeText) this.nudgeText.innerHTML = nudge.html;

      /* Price block — frozen at min values below the minimum */
      if (this.priceLine1) {
        this.priceLine1.textContent = this.formatText(this.config.copy.priceLine1, {
          cans: effQty,
          pouches: effQty * this.config.pouchesPerCan
        });
      }
      if (this.priceLine2) {
        this.priceLine2.textContent = this.formatText(this.config.copy.priceLine2, {
          rate: this.perCanRate(effQty, plan).toFixed(2)
        });
      }
      if (this.priceCompare) this.priceCompare.textContent = this.money(this.compareAt(effQty));
      if (this.priceTotal) this.priceTotal.textContent = this.money(this.planTotal(effQty, plan));
      if (this.priceBlock) this.priceBlock.classList.toggle('is-frozen', frozen);

      /* ATC */
      if (this.atc) this.atc.disabled = frozen;
      if (this.atcLabel) {
        if (frozen) {
          const remaining = this.minCans - total;
          this.atcLabel.textContent = this.formatText(this.config.copy.atcDisabled, {
            n: remaining,
            unitCap: remaining === 1 ? 'Can' : 'Cans'
          });
        } else {
          this.atcLabel.textContent = this.formatText(this.config.copy.atcEnabled, { qty: total });
        }
      }
    }

    formatText(template, tokens) {
      let out = String(template);
      Object.keys(tokens).forEach((key) => {
        out = out.split('{' + key + '}').join(String(tokens[key]));
      });
      return out;
    }

    /* ---------- events ---------- */
    bindEvents() {
      this.toggleBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.plan = btn.dataset.pfPlan === 'onetime' ? 'onetime' : 'subscribe';
          this.toggleBtns.forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          this.render();
        });
      });

      this.tierCards.forEach((card) => {
        card.addEventListener('click', () => {
          const target = parseInt(card.dataset.tierCans, 10);
          if (target) this.setPackTo(target);
        });
      });

      this.rows.forEach((row) => {
        row.querySelectorAll('.pf-pdp__step').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = parseInt(btn.dataset.step, 10) || 0;
            this.setQty(row, this.qtyOf(row) + delta);
            this.render();
          });
        });
      });

      if (this.atc) {
        this.atc.addEventListener('click', () => this.addToCart());
      }
    }

    /* ---------- cart ---------- */
    findVariant(name, explicitId) {
      const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (explicitId) {
        const byId = this.variants.find((v) => String(v.id) === String(explicitId));
        if (byId) return byId;
      }
      const n = norm(name);
      return (
        this.variants.find((v) => {
          const vt = norm(v.title);
          return vt === n || vt.indexOf(n) !== -1 || n.indexOf(vt) !== -1;
        }) || null
      );
    }

    pickPlan(variant) {
      if (!variant || !variant.plans || !variant.plans.length) return null;
      if (this.overridePlan) {
        const match = variant.plans.find((p) => p.id === this.overridePlan);
        if (match) return match.id;
      }
      const preferred = variant.plans.find((p) => /month|4 ?week|15 ?%|15off/i.test(p.name || ''));
      return (preferred || variant.plans[0]).id;
    }

    buildCartItems() {
      /* One line item per flavor with qty > 0, on the selected plan.
         INTEGRATION POINT — tiered charged total: the graduated ladder price
         is NOT expressed here (line items carry the product's own price). The
         mechanism that makes the charged total match the ladder to the cent
         and persists on ReCharge recurring orders (Shopify Function, per-line
         pricing, discount, etc.) is owned by the developer/backend. If that
         mechanism needs line attributes / a discount code, attach them here. */
      const items = [];
      let missing = false;
      this.rows.forEach((row) => {
        const qty = this.qtyOf(row);
        if (qty <= 0) return;
        const variant = this.findVariant(row.dataset.flavorName, row.dataset.variantId);
        if (!variant) {
          missing = true;
          return;
        }
        const item = { id: variant.id, quantity: qty };
        if (this.plan === 'subscribe') {
          const planId = this.pickPlan(variant);
          if (planId) item.selling_plan = planId;
        }
        items.push(item);
      });
      return { items, missing };
    }

    addToCart() {
      if (this.atc.disabled) return;
      const { items, missing } = this.buildCartItems();
      if (missing) {
        console.warn('[pure-focus-pdp] Could not map every flavor to a variant. Check variants or set a Variant ID.');
      }
      if (!items.length) return;

      this.atc.classList.add('is-loading');
      const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      })
        .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
        .then((res) => {
          this.atc.classList.remove('is-loading');
          if (!res.ok) {
            console.warn('[pure-focus-pdp] add to cart failed', res.data);
            return;
          }
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: res.data, sourceId: 'pure-focus-pdp', data: { source: 'pure-focus-pdp' } }
            })
          );
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.open === 'function') {
            try {
              drawer.open();
            } catch (e) {
              /* no-op */
            }
          }
        })
        .catch((err) => {
          this.atc.classList.remove('is-loading');
          console.warn('[pure-focus-pdp] add to cart error', err);
        });
    }
  }

  /* ------------------------------------------------------------------
     Ancillary PDP behaviours (gallery, accordion, ingredient popup,
     carousel, sticky CTA, delivery date). Scoped to the same root.
  ------------------------------------------------------------------ */
  function initAncillary(root) {
    /* Gallery */
    const media = root.querySelector('.pf-pdp__media');
    if (media) {
      const slides = Array.from(media.querySelectorAll('.pf-pdp__slide'));
      const thumbs = Array.from(root.querySelectorAll('.pf-pdp__thumb'));
      const count = slides.length;
      let active = 0;
      const show = (idx) => {
        if (!count) return;
        active = (idx + count) % count;
        media.dataset.active = active;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === active));
        thumbs.forEach((thumb, i) => {
          const on = i === active;
          thumb.classList.toggle('is-active', on);
          thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      };
      const prev = media.querySelector('.pf-pdp__arrow--prev');
      const next = media.querySelector('.pf-pdp__arrow--next');
      if (prev) prev.addEventListener('click', () => show(active - 1));
      if (next) next.addEventListener('click', () => show(active + 1));
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    }

    /* Accordion */
    const accordion = root.querySelector('[data-pf-accordion]');
    if (accordion) {
      const items = Array.from(accordion.querySelectorAll('.pf-pdp__acc-item'));
      items.forEach((item) => {
        const trigger = item.querySelector('.pf-pdp__acc-trigger');
        if (!trigger) return;
        trigger.addEventListener('click', () => {
          const open = item.classList.contains('is-open');
          items.forEach((other) => {
            other.classList.remove('is-open');
            const t = other.querySelector('.pf-pdp__acc-trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
          });
          if (!open) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }

    /* Ingredients popup */
    const popup = root.querySelector('[data-pf-popup]');
    if (popup) {
      const open = () => {
        popup.hidden = false;
        document.body.style.overflow = 'hidden';
      };
      const close = () => {
        popup.hidden = true;
        document.body.style.overflow = '';
      };
      root.querySelectorAll('[data-pf-open-popup]').forEach((btn) => btn.addEventListener('click', open));
      popup.querySelectorAll('[data-pf-close-popup]').forEach((btn) => btn.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !popup.hidden) close();
      });
    }

    /* Ingredients carousel */
    const carousel = root.querySelector('[data-pf-carousel]');
    if (carousel) {
      const track = carousel.querySelector('[data-pf-carousel-track]');
      const cards = track ? Array.from(track.querySelectorAll('.pf-pdp__ing-card')) : [];
      const dots = Array.from(carousel.querySelectorAll('[data-pf-carousel-dot]'));
      const prevBtn = carousel.querySelector('[data-pf-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-pf-carousel-next]');
      let current = 0;
      const clamp = (i) => Math.max(0, Math.min(cards.length - 1, i));
      const setActive = (idx) => {
        current = clamp(idx);
        dots.forEach((d, i) => {
          const on = i === current;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (prevBtn) prevBtn.disabled = current <= 0;
        if (nextBtn) nextBtn.disabled = current >= cards.length - 1;
      };
      const scrollToIndex = (idx) => {
        idx = clamp(idx);
        if (!cards.length) return;
        track.scrollTo({ left: cards[idx].offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
        setActive(idx);
      };
      if (prevBtn) prevBtn.addEventListener('click', () => scrollToIndex(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => scrollToIndex(current + 1));
      dots.forEach((d, i) => d.addEventListener('click', () => scrollToIndex(i)));
      if (track) {
        let scrollTimer;
        track.addEventListener('scroll', () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => {
            if (cards.length < 2) return;
            const step = cards[1].offsetLeft - cards[0].offsetLeft;
            if (step > 0) setActive(Math.round(track.scrollLeft / step));
          }, 80);
        });
      }
      root.querySelectorAll('[data-pf-open-popup]').forEach((btn) =>
        btn.addEventListener('click', () => {
          if (track) track.scrollLeft = 0;
          setActive(0);
        })
      );
      setActive(0);
    }

    /* Sticky CTA (mobile + desktop) */
    const sticky = root.querySelector('[data-pf-sticky]');
    if (sticky && 'IntersectionObserver' in window) {
      const targetId = sticky.getAttribute('data-pf-sticky-target');
      const target = targetId ? document.getElementById(targetId) : null;
      const trigger = root.querySelector('[data-pf-atc]') || target;
      if (target && trigger) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const above = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              sticky.classList.toggle('is-visible', above);
            });
          },
          { threshold: 0 }
        );
        observer.observe(trigger);
        const btn = sticky.querySelector('[data-pf-sticky-btn]');
        if (btn) btn.addEventListener('click', () => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    }

    /* Delivery date */
    const dateEl = root.querySelector('[data-pf-delivery-date]');
    if (dateEl) {
      let leadDays = parseInt(dateEl.dataset.leadDays, 10);
      if (isNaN(leadDays)) leadDays = 2;
      let cutoffHour = parseInt(dateEl.dataset.cutoffHour, 10);
      if (isNaN(cutoffHour)) cutoffHour = 14;
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const ordinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const d = new Date();
      let lead = leadDays;
      if (d.getHours() >= cutoffHour) lead += 1;
      let added = 0;
      while (added < lead) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      dateEl.textContent = months[d.getMonth()] + ' ' + ordinal(d.getDate());
    }
  }

  /* ------------------------------------------------------------------
     Cart minimum enforcement (existing master-task rule).

     NOTE — cart re-pricing on quantity change (new scope rule) is part of the
     developer-owned pricing mechanism: re-pricing the actual Pure Focus lines
     so the group total matches the ladder cannot be done from theme JS alone
     (line prices are server-side). This module keeps the minimum-removal rule;
     wire the re-pricing to the chosen backend mechanism.
  ------------------------------------------------------------------ */
  function showCartMinToast(cansRequired) {
    let toast = document.querySelector('.pf-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'pf-cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = 'Minimum order is ' + cansRequired + ' cans — items removed.';
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.clearTimeout(toast.__pfTimer);
    toast.__pfTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 4000);
  }

  function initCartMinimum(root) {
    if (window.__pfCartMinInit) return;
    const configEl = root.querySelector('[data-pf-build-pack]');
    const productId = configEl ? parseInt(configEl.dataset.productId, 10) : NaN;
    const cfg = (() => {
      try {
        return JSON.parse(root.querySelector('[data-pf-config]').textContent);
      } catch (e) {
        return {};
      }
    })();
    const cansRequired = cfg.minCans || 3;
    if (!productId) return;
    window.__pfCartMinInit = true;

    let busy = false;
    let timer = null;
    const routeUrl = (key, fallback) => (window.routes && window.routes[key] ? window.routes[key] : fallback);

    const enforce = () => {
      if (busy) return;
      busy = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const lines = (cart.items || []).filter((it) => it.product_id === productId);
          const totalCans = lines.reduce((s, it) => s + (it.quantity || 0), 0);
          if (totalCans <= 0 || totalCans >= cansRequired) return null;
          const updates = {};
          lines.forEach((it) => {
            updates[it.key] = 0;
          });
          return fetch(routeUrl('cart_update_url', '/cart/update') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          }).then(() => {
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'pure-focus-pdp-min' } }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            showCartMinToast(cansRequired);
          });
        })
        .catch(() => {})
        .then(() => {
          busy = false;
        });
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(enforce, 300);
    };

    ['cart:update', 'cart:refresh', 'cart:added', 'cart:change'].forEach((evt) => document.addEventListener(evt, schedule));

    if (typeof window.fetch === 'function' && !window.fetch.__pfWrapped) {
      const origFetch = window.fetch;
      const wrapped = function (input) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const result = origFetch.apply(this, arguments);
        if (/\/cart\/(change|update|add|clear)(\.js)?(\?|$)/i.test(url)) {
          result.then(() => schedule()).catch(() => {});
        }
        return result;
      };
      wrapped.__pfWrapped = true;
      window.fetch = wrapped;
    }
  }

  /* ---------- boot ---------- */
  function initPdp(root) {
    if (!root || root.dataset.pfInit === '1') return;
    root.dataset.pfInit = '1';
    if (root.querySelector('[data-pf-build-pack]')) {
      /* eslint-disable no-new */
      new PureFocusPdp(root);
    }
    initAncillary(root);
    initCartMinimum(root);
  }

  function initAll() {
    document.querySelectorAll('.pf-pdp').forEach(initPdp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector ? e.target.querySelector('.pf-pdp') : null;
    if (root) {
      root.dataset.pfInit = '';
      initPdp(root);
    }
  });
})();
